import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { FormattedMessage, useIntl } from "react-intl";

import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import { LoadingSpinner } from "@mapmycustomers/ui";

import { showGlobalCreateActivityModal } from "@app/component/createEditEntity/Activity/store/actions";
import DashCard from "@app/component/DashCard";
import CardCta from "@app/component/DashCard/CardCta";
import SelectField from "@app/component/input/SelectField";
import MoreButton from "@app/component/MoreButton";
import SelectActivityTypesModal from "@app/component/SelectEntityDialog/SelectActivityTypesModal";
import { ActivityCount, SIMPLE_DATE_RANGE_LABELS } from "@app/scene/home/component/Cards/util";
import { getActivityByRepData, isActivityByRepDataLoading } from "@app/scene/home/store";
import { fetchActivityByRepData } from "@app/scene/home/store/actions";
import analytics from "@app/scene/reports/reportsAnalytics";
import { getActivityTypes } from "@app/store/activity";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import defaultIdGetter from "@app/util/defaultIdGetter";
import defaultNodeNameGetter from "@app/util/defaultNodeNameGetter";

import styles from "./ActivityReps.module.scss";
import useActivitiesChartConfig from "./useActivitiesChartConfig";

const chartStyles = {
  width: "100%",
};

const DEFAULT_CHART_CONFIG = Object.freeze({
  colors: ["#ff9b00", "#ff5858", "#3064e4", "#37cdcd", "#24b375", "#575bff"],
  credits: {
    enabled: false,
  },
  legend: {
    enabled: true,
  },
  title: null,
  tooltip: {
    useHtml: true,
  },
  xAxis: {
    visible: true,
  },
  yAxis: {
    allowDecimals: false,
    endOnTick: true,
    labels: {
      enabled: true,
    },
    maxPadding: 0.05,
    tickAmount: 5,
    title: null,
  },
});

interface Props {
  activityByRepData: Record<User["id"], ActivityCount[]>;
  activityTypes: ActivityType[];
  className?: string;
  fetchActivityByRepData: typeof fetchActivityByRepData.request;
  hasActivities: boolean;
  isLoading: boolean;
  isReadOnlyMember: boolean;
  onShowCreateActivityModal: typeof showGlobalCreateActivityModal;
  selectedUsers: User[];
  users: User[];
}

const ActivityRepsCard: React.FC<Props> = ({
  activityByRepData,
  activityTypes,
  className,
  fetchActivityByRepData,
  hasActivities,
  isLoading,
  isReadOnlyMember,
  onShowCreateActivityModal,
  selectedUsers,
  users,
}) => {
  const intl = useIntl();

  const [selectedActivityTypes, setActivityTypes] = useState<ActivityType[]>(activityTypes);
  const [dateRange, setDateRange] = useState(
    SIMPLE_DATE_RANGE_LABELS.length ? SIMPLE_DATE_RANGE_LABELS[0].value : ""
  );

  const [activityTypesDialogVisible, showActivityTypesDialog, hideActivityTypesDialog] =
    useBoolean();

  const handleChangeDateRange = useCallback(
    (value) => {
      setDateRange(`${value}`);
      analytics.feature(["Activities By Rep", "Date Range"], { range: value });
    },
    [setDateRange]
  );

  useChangeTracking(
    () =>
      analytics.feature(["Activities by Rep", "Select Activity types"], {
        types: selectedActivityTypes,
      }),
    [selectedActivityTypes]
  );

  useEffect(() => {
    fetchActivityByRepData({
      activityTypeIds: selectedActivityTypes.map(({ id }) => id),
      assigneeIds: selectedUsers.map(({ id }) => id),
      dateRange,
    });
  }, [fetchActivityByRepData, selectedUsers, selectedActivityTypes, dateRange]);

  const hasFilteredActivities = Object.keys(activityByRepData).length > 0;

  const chartConfig = useActivitiesChartConfig(
    DEFAULT_CHART_CONFIG,
    hasFilteredActivities ? activityByRepData : undefined,
    dateRange,
    users
  );

  return (
    <DashCard
      actions={[
        <SelectField<string>
          dropdownMatchSelectWidth={false}
          key="date-range"
          label={intl.formatMessage({
            id: "home.cards.activityReps.selectField.label",
            defaultMessage: "Date Range",
            description: "Label for Select Field in Activity by Rep Card",
          })}
          labelClassName={styles.filterLabel}
          onChange={handleChangeDateRange}
          options={SIMPLE_DATE_RANGE_LABELS}
          value={dateRange}
        />,
        <Dropdown
          key="menu"
          overlay={
            <Menu>
              <Menu.Item onClick={() => showActivityTypesDialog()}>
                {intl.formatMessage({
                  id: "home.cards.activityReps.dropdown.menuItem.selectActivityTypes",
                  defaultMessage: "Select Activity Types...",
                  description: "Title for the select activity types menu item",
                })}
              </Menu.Item>
            </Menu>
          }
          placement="bottomRight"
          trigger={["click"]}
        >
          <MoreButton />
        </Dropdown>,
      ]}
      className={className}
      title={intl.formatMessage({
        id: "home.cards.activityReps.title",
        defaultMessage: "Activities by Rep",
        description: "Activity by Reps Card Title",
      })}
    >
      {isLoading && <LoadingSpinner />}
      {!isLoading && !hasFilteredActivities && !hasActivities && (
        <CardCta
          buttonLabel={intl.formatMessage({
            id: "home.cards.activityReps.cardCta.addActivity",
            defaultMessage: "Add Activity",
            description: "Activity by Reps Card CTA button label",
          })}
          buttonVisible={!isReadOnlyMember}
          onClick={() => onShowCreateActivityModal(undefined)}
          text={intl.formatMessage({
            id: "home.cards.activityReps.cta.noActivitiesFound",
            defaultMessage: "No activities found. Add an activity or change filters.",
            description: "Activity by Reps Card CTA text for add your first activity",
          })}
        >
          <a
            href="https://support.mapmycustomers.com/hc/en-us/articles/360050260013-Activities-Overview"
            rel="noopener noreferrer"
            target="_blank"
          >
            <FormattedMessage
              id="home.cards.activityReps.cta.learnMore"
              defaultMessage="Learn more about activities"
              description="Activity by Reps Card CTA text for learn more link"
            />
          </a>
        </CardCta>
      )}
      {!isLoading && !hasFilteredActivities && hasActivities && (
        <CardCta
          buttonLabel={intl.formatMessage({
            id: "home.cards.activityReps.cardCta.addActivity",
            defaultMessage: "Add Activity",
            description: "Activity by Reps Card CTA button label",
          })}
          buttonVisible={!isReadOnlyMember}
          onClick={() => onShowCreateActivityModal(undefined)}
          text={intl.formatMessage({
            id: "home.cards.activityReps.cta.noActivitiesFound",
            defaultMessage: "No activities found. Add an activity or change filters.",
            description: "Activity by Reps Card CTA text for add your first activity",
          })}
        >
          <a
            href="https://support.mapmycustomers.com/hc/en-us/articles/360050260013-Activities-Overview"
            rel="noopener noreferrer"
            target="_blank"
          >
            <FormattedMessage
              id="home.cards.activityReps.cta.learnMore"
              defaultMessage="Learn more about activities"
              description="Activity by Reps Card CTA text for learn more link"
            />
          </a>
        </CardCta>
      )}
      {!isLoading && hasFilteredActivities && (
        <HighchartsReact
          containerProps={{ style: chartStyles }}
          highcharts={Highcharts}
          isPureConfig
          options={chartConfig}
        />
      )}
      <SelectActivityTypesModal
        entities={activityTypes}
        getDisplayName={defaultNodeNameGetter}
        getKey={defaultIdGetter}
        onConfirm={setActivityTypes}
        onHide={hideActivityTypesDialog}
        selectedEntities={selectedActivityTypes}
        title={intl.formatMessage({
          id: "home.cards.activityReps.selectActivityTypesDialog.title",
          defaultMessage: "Select Activity Types",
          description: "Title for select activity types dialog",
        })}
        visible={activityTypesDialogVisible}
      />
    </DashCard>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityByRepData: getActivityByRepData(state),
  activityTypes: getActivityTypes(state),
  // TODO: fetch count of activities later
  hasActivities: true, // getHasActivities(state),
  isLoading: isActivityByRepDataLoading(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  fetchActivityByRepData: fetchActivityByRepData.request,
  onShowCreateActivityModal: showGlobalCreateActivityModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityRepsCard);
